import { Calendar, Package, Zap, Code, Wrench, Bug, ArrowRight, Sparkles, CheckCircle2, Clock, TrendingUp, Image, Database, Braces, Lock, Palette, Settings, Layout, FileText, Type, RefreshCw } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import changelogData from '@/data/changelog.json';

interface ChangelogEntry {
  version: string;
  date: string;
  sections: {
    title: string;
    icon: string;
    type: 'added' | 'changed' | 'fixed' | 'removed';
    items: {
      category?: string;
      title: string;
      description?: string;
      subItems?: string[];
    }[];
  }[];
  stats?: {
    label: string;
    value: string;
    trend?: 'up' | 'down' | 'neutral';
  }[];
}

const iconMap = {
  Zap,
  Code,
  Bug,
  Sparkles,
  Wrench,
  Image,
  Database,
  Braces,
  Lock,
  Palette,
  Settings,
  Layout,
  FileText,
  Type,
  RefreshCw,
};

const typeColors = {
  added: 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-950/30',
  changed: 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/30',
  fixed: 'text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-950/30',
  removed: 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/30',
};

const trendIcons = {
  up: <TrendingUp className="h-3 w-3 text-green-600 dark:text-green-400" />,
  down: <TrendingUp className="h-3 w-3 text-red-600 dark:text-red-400 rotate-180" />,
  neutral: null,
};

export function Changelog() {
  const changelog = changelogData as ChangelogEntry[];

  // Sort by date (newest first) and find the latest entry
  const sortedChangelog = [...changelog].sort((a, b) => {
    return new Date(b.date).getTime() - new Date(a.date).getTime();
  });

  const latestVersion = sortedChangelog[0]?.version;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-3">
          <Calendar className="h-8 w-8 text-primary" />
          Changelog
        </h1>
        <p className="text-muted-foreground mt-2">
          All notable changes to the kcTools project are documented here.
        </p>
      </div>

      {/* Changelog Entries */}
      <div className="space-y-8">
        {sortedChangelog.map((entry, entryIndex) => {
          const isLatest = entry.version === latestVersion;

          return (
            <Card
              key={entryIndex}
              className={`p-6 ${isLatest ? 'border-primary/50 shadow-lg' : ''}`}
            >
              {/* Version Header */}
              <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
                <div className="flex items-center gap-3">
                  <Package className="h-6 w-6 text-primary" />
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="text-2xl font-bold">{entry.version}</h2>
                      {isLatest && (
                        <Badge variant="default" className="gap-1">
                          <Sparkles className="h-3 w-3" />
                          Latest
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground flex items-center gap-1.5 mt-1">
                      <Clock className="h-3.5 w-3.5" />
                      {entry.date}
                    </p>
                  </div>
                </div>

              {/* Stats */}
              {entry.stats && (
                <div className="flex flex-wrap gap-3">
                  {entry.stats.map((stat, statIndex) => (
                    <div
                      key={statIndex}
                      className="text-center px-4 py-2 rounded-lg bg-muted/50"
                    >
                      <div className="text-xs text-muted-foreground mb-0.5">{stat.label}</div>
                      <div className="font-bold text-lg flex items-center justify-center gap-1">
                        {stat.value}
                        {stat.trend && trendIcons[stat.trend]}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Separator className="mb-6" />

            {/* Sections */}
            <div className="space-y-8">
              {entry.sections.map((section, sectionIndex) => {
                const IconComponent = iconMap[section.icon as keyof typeof iconMap];

                return (
                  <div key={sectionIndex}>
                    {/* Section Header */}
                    <div className="flex items-center gap-2 mb-4">
                      <div className={`p-1.5 rounded-lg ${typeColors[section.type]}`}>
                        {IconComponent && <IconComponent className="h-4 w-4" />}
                      </div>
                      <h3 className="text-xl font-semibold">{section.title}</h3>
                    </div>

                    {/* Items */}
                    <div className="space-y-4 ml-8">
                      {section.items.map((item, itemIndex) => (
                        <div key={itemIndex} className="space-y-2">
                          {/* Category Badge */}
                          {item.category && (
                            <Badge variant="outline" className="mb-1">
                              {item.category}
                            </Badge>
                          )}

                          {/* Item Title & Description */}
                          <div className="flex items-start gap-2">
                            <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                            <div className="space-y-1 flex-1">
                              <p className="font-medium">{item.title}</p>
                              {item.description && (
                                <p className="text-sm text-muted-foreground">{item.description}</p>
                              )}

                              {/* Sub-items */}
                              {item.subItems && item.subItems.length > 0 && (
                                <ul className="space-y-1 mt-2 ml-4">
                                  {item.subItems.map((subItem, subIndex) => (
                                    <li
                                      key={subIndex}
                                      className="text-sm text-muted-foreground flex items-start gap-2"
                                    >
                                      <ArrowRight className="h-3.5 w-3.5 mt-0.5 shrink-0 text-primary/60" />
                                      <span>{subItem}</span>
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        );
        })}
      </div>

      {/* Footer Note */}
      <Card className="p-6 bg-muted/30">
        <div className="flex items-start gap-3">
          <Wrench className="h-5 w-5 text-primary mt-0.5" />
          <div>
            <p className="font-medium mb-1">Changelog Format</p>
            <p className="text-sm text-muted-foreground">
              This changelog is based on{' '}
              <a
                href="https://keepachangelog.com/en/1.0.0/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                Keep a Changelog
              </a>
              . Full change history is available in the{' '}
              <code className="px-1.5 py-0.5 rounded bg-muted text-xs">CHANGELOG.md</code> file.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
}
